import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { PDFDocument } from "pdf-lib";
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

import {
  Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label,
  CardHeader, Alert, Spinner, Input, FormFeedback
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; 
import { updateDocument } from '../../../slices/AIDoc/thunk';
import { uploadCaseFile } from '../../../slices/thunks';
import { fetchParentCategories } from '../../../slices/thunks';
import { clearUploadedDocumentResponse, clearUploadError } from '../../../slices/AIDoc/reducer';
import { getAuthUser } from '../../../helpers/utils';
import { debounce } from "lodash";

// +++ ADDED: Register FilePond plugins +++
registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
);

export const MAX_PAGES = 100000;
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

// --- Main Component ---
const CaseUpload = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = getAuthUser();

  const isRTL = i18n.dir() === "rtl";

  const [filepondFiles, setFilepondFiles] = useState([]); 
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(0); // 0 - no action, 1 full file, 2 complete, 3 partial commplete,
  const [processingLog, setProcessingLog] = useState([]);
  const [activeStep, setActiveStep] = useState(2); 
  const [isGeneratingDetails, setIsGeneratingDetails] = useState(false);
  const [isFileValid, setIsFileValid] = useState(false);

  const [isMultiUpload, setIsMultiUpload] = useState(true);
  const [processedFiles, setProcessedFiles] = useState([]);

  const processedFilesRef = useRef([]);
  const filepondRef = useRef(null);
  const completedFilesCount = useRef(0);

    // --- Split UI state ---
    const [showSplitUI, setShowSplitUI] = useState(false);
    const [splitRanges, setSplitRanges] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [originalFileName, setOriginalFileName] = useState("");

  // --- REDUX STATE ---
  const {
    documentUploadError,
    loading: categoriesLoading,
    uploadProgress,
  } = useSelector((state) => state.AIDoc);

  // --- FORMIK & YUP INTEGRATION ---
  const validationSchema = Yup.object().shape({
    selectedCategory: Yup.object().nullable(),
    selectedSubCategory: Yup.object().nullable(),
  });
  
  const formik = useFormik({
    initialValues: {
        selectedCategory: null,
        selectedSubCategory: null,
    },
    validationSchema: validationSchema,
    // --- onSubmit is no longer needed as saving is programmatic ---
    onSubmit: async (values) => {
        // This logic is now handled inside handleAnalyze
        console.log("Form submission is disabled. Saving is handled by the Analyze process.");
    },
  });

  const handleClear = useCallback(() => {
    // setActiveStep(1);
    setActiveStep(2);
    if (filepondRef.current) {
        filepondRef.current.removeFiles();
    }
    setIsFileValid(false);
    setFilepondFiles([]);
    setIsAnalyzed(false);
    setIsCompleted(0);
    setProcessingLog([]);
    setIsGeneratingDetails(false);
    dispatch(clearUploadedDocumentResponse());
    formik.resetForm();
    setIsMultiUpload(true);
    setProcessedFiles([]);
    setShowSplitUI(false);
    setSplitRanges([]);
    setTotalPages(0);
    setOriginalFileName("");
    completedFilesCount.current = 0;
    processedFilesRef.current = [];
  }, [dispatch, formik]);


  const handleComplete = () => {
    setIsCompleted(2);
    setIsGeneratingDetails(false);
  }

  const handleFilePondUpdate = useRef(
    debounce(async (fileItems) => {

      const selectedFiles = fileItems.map(item => item.file).filter(Boolean);
      if (selectedFiles.length === 0) return;

      setFilepondFiles(fileItems);
      setIsFileValid(true);

      try {
        setIsCompleted(1);
        setActiveStep(4);
        await uploadSmallFile(fileItems, selectedFiles);
        setIsGeneratingDetails(true);

      } catch (err) {
        console.error("PDF validation failed:", err);
        toast.error(t("Unable to validate one or more PDFs."));

        if (filepondRef.current) {
          fileItems.forEach((fileItem) => {
            filepondRef.current.removeFile(fileItem.id);
          });
        }
      }
    }, 600)
  ).current;

  // +++ ADDED: Handler for FilePond warnings (like total size exceeded) +++
  const handleFilePondWarning = (error, file, status) => {
    if (error.body === 'Total file size has been exceeded') {
        const message = isRTL 
            ? `لقد تم تجاوز الحجم الإجمالي للملفات. الحد الأقصى هو ${MAX_FILE_SIZE / (1024*1024)} ميجا بايت.`
            : `Total file size exceeded. The limit is ${MAX_FILE_SIZE / (1024*1024)} MB.`;
        toast.warn(message); 
        return false; 
    }
    return true;
  };

  const handleRange = async () => {
    const lastRange = splitRanges[splitRanges.length - 1];
    const lastTo = lastRange?.to ? parseInt(lastRange.to) : 0;

    const fromPage = lastTo + 1;
    const toPage = lastTo + MAX_PAGES;

    setSplitRanges([...splitRanges, {
      filename: `${originalFileName.replace('.pdf', '')}_${splitRanges.length + 1}.pdf`,
      from: Math.min(totalPages, fromPage),
      to: Math.min(totalPages, toPage)
    }])
  }

  const uploadSmallFile = (originalFile, pdfFiles = []) => {
    dispatch(clearUploadError());
    setProcessingLog([]);

    const onProgress = ({ event, data }) => {
      try {
        const parsedData = typeof data === "string" ? JSON.parse(data) : data;

        if (event === "init") {
          setProcessedFiles(pdfFiles.map(f => ({
            id: Date.now() + Math.random(),
            name: f.name,
            status: "analyzing",
            message: parsedData.message || "Starting analysis..."
          })));
          return;
        }

        if (event === "error") {
          setProcessedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: "error",
              message: parsedData.message
            }))
          );
          setIsCompleted(2);
          setIsGeneratingDetails(false);
          return;
        }
           
        if (event === "duplicate") {
          setProcessedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: "error",
              message: parsedData.message || t('duplicateFile')
            }))
          );
          setIsCompleted(2);
          setIsGeneratingDetails(false);
          return;
        }

        // if (event === "file_ocr_done") {
        //   const data = {
        //     DocumentID: parsedData.DBID,
        //     OriginFileName: parsedData.text?.originalFileName,   
        //     Tags: parsedData.text?.tags
        //   }
        //   dispatch(updateDocument(data))
        // }

        if (event === "other_data") {
          setProcessedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: "done",
              message: parsedData.message || "Processing complete"
            }))
          );
          setIsCompleted(2);
          setIsGeneratingDetails(false);
        }
      } catch (err) {
        console.error("Failed to process chunk event:", err, data);
        setIsGeneratingDetails(false);
      }
    };

    // Prepare FormData for the initial POST request
    const formData = new FormData();
     if (Array.isArray(pdfFiles)) {
      pdfFiles.map((file) => {
        formData.append("file", file);
      })
    }
    formData.append("UserID", user.ID);
    formData.append("StatusID", 2);
    // formData.append("CaseType", formik.values.selectedCaseType?.CaseType);
    // formData.append("CaseTypeID", formik.values.selectedCaseType?.value);
    // formData.append("YearFrom", formik.values.yearFrom || "");
    // formData.append("YearTo", formik.values.yearTo || "");
    // formData.append("IsVisible", formik.values.isPublic);
    formData.append("IsVisible", true);


    dispatch(uploadCaseFile({ formData, onProgress }));
  };

  const handleSplitAndAnalyze = async () => {
    const file = filepondFiles[0]?.file;
    if (!file) return;

    const pdfBytes = await file.arrayBuffer();
    const pdf = await PDFDocument.load(pdfBytes);

    const pdfChunks = [];
    for (const range of splitRanges) {
      const newPdf = await PDFDocument.create();
      const from = Math.max(1, range.from);
      const to = Math.min(totalPages, range.to);

      const pages = await newPdf.copyPages(
        pdf,
        Array.from({ length: to - from + 1 }, (_, i) => i + (from - 1))
      );
      pages.forEach((p) => newPdf.addPage(p));

      const newPdfBytes = await newPdf.save();
      const blob = new Blob([newPdfBytes], { type: "application/pdf" });

      pdfChunks.push(new File([blob], range.filename, { type: "application/pdf" }));
    }

    setShowSplitUI(false);
    setActiveStep(4);

    // Phase 1: upload full file first
    uploadLargeFile(file, pdfChunks);

    setIsGeneratingDetails(true);
  };

  const uploadLargeFile = (originalFile, pdfChunks) => {

    const onProgress = ({ event, data }) => {
      try {
        const parsedData = typeof data === "string" ? JSON.parse(data) : data;

        if (event === "file_ocr_done") {
          const dbid = parsedData.DBID;
          const data = {
            DocumentID: parsedData.DBID,
            OriginFileName: parsedData.text?.originalFileName,   
            Tags: parsedData.text?.tags
          }
          dispatch(updateDocument(data))
          setIsCompleted(1);

          // now start phase 2: upload parts with DBID
          handleAnalyze(pdfChunks, dbid);
        }
      } catch (err) {
        console.error("Failed to process initial file:", err, data);
      }
    };

    // Prepare FormData for the initial POST request
    const formData = new FormData();
    formData.append("file", originalFile);
    formData.append("UserID", user.ID);
    formData.append("StatusID", 2);
    // formData.append("CaseType", formik.values.selectedCaseType?.CaseType);
    // formData.append("CaseTypeID", formik.values.selectedCaseType?.value);
    // formData.append("YearFrom", formik.values.yearFrom || "");
    // formData.append("YearTo", formik.values.yearTo || "");
    // formData.append("IsVisible", formik.values.isPublic);
    formData.append("IsVisible", true);

    dispatch(uploadCaseFile({ formData, onProgress }));
  };

  const handleAnalyze = (pdfFiles, dbid) => {
    if (!pdfFiles.length || !dbid) {
      toast.error("Cannot analyze. Missing chunks or DBID.");
      return;
    }

    dispatch(clearUploadError());
    setProcessingLog([]);
    setIsGeneratingDetails(true);

    const formData = new FormData();

    // append all files
    pdfFiles.forEach((file) => {
      formData.append("file", file);
    });

    formData.append("DBID", dbid);
    formData.append("UserID", user.ID);
    formData.append("StatusID", 2);
    // formData.append("CaseType", formik.values.selectedCaseType?.CaseType);
    // formData.append("CaseTypeID", formik.values.selectedCaseType?.value);
    // formData.append("YearFrom", formik.values.yearFrom || "");
    // formData.append("YearTo", formik.values.yearTo || "");
    // formData.append("IsVisible", formik.values.isPublic);
    formData.append("IsVisible", true);


    const onProgress = ({ event, data }) => {
      try {
        const parsedData = typeof data === "string" ? JSON.parse(data) : data;

        if (event === "init") {
          setProcessedFiles(pdfFiles.map(f => ({
            id: Date.now() + Math.random(),
            name: f.name,
            status: "analyzing",
            message: parsedData.message || "Starting analysis..."
          })));
          return;
        }

        if (event === "error" || event === "duplicate") {
          setProcessedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: "error",
              message: parsedData.message
            }))
          );
          setIsCompleted(3);
          return;
        }

        if (event === "other_data") {
          const data = {
            DocumentID: parsedData.DBID,
            FileName: parsedData?.filename,   
            OriginalFileName: parsedData?.originalFileName,   
          }
          dispatch(updateDocument(data))
          .unwrap()
          .then(() => {
            navigate('/ai/case');
          })
          .catch((error) => {
            console.error("Metadata save failed:", error);
          });
        }

        if (event === "file_processed") {
          setProcessedFiles(prev =>
            prev.map(f => ({
              ...f,
              status: "done",
              message: parsedData.message || "Processing complete"
            }))
          );
          setIsCompleted(2);
          setIsGeneratingDetails(false);
        }
      } catch (err) {
        console.error("Failed to process chunk event:", err, data);
        setIsGeneratingDetails(false);
      }
    };

    // single dispatch for all files
    dispatch(uploadCaseFile({ formData, onProgress }));
  };


  const handleRetry = (file) => {
    // Reset status to pending
    file.id = Date.now() + processedFiles.length;
    file.status = 'pending';
    file.message = "WaitingInQueue";

    const updatedProcessedFiles = [
      ...processedFiles.map(f =>
        f.id === file.id
          ? { ...f, status: "retry", message: "Retrying upload..." }
          : f
      ),
      file // ⬅️ append new file at end
    ]

    processedFilesRef.current = updatedProcessedFiles;
    setProcessedFiles(updatedProcessedFiles);
  };

  // useEffect(() => {
  //   dispatch(fetchParentCategories({ DocType: 4 })); 
  // }, [dispatch]);

  useEffect(() => {
    return () => {
      handleClear();
      dispatch(clearUploadedDocumentResponse());  
    };
  }, []);
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
            <h4 className="mb-4">{t("Add Case")}</h4>
            <Card>
                <CardHeader className="d-flex align-items-center">
                    <Button color="secondary" className="me-2" onClick={() => navigate("/ai/case")}>
                        <i className={`${!isRTL ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                    </Button>
                    {/* <h5 className="card-title mb-0 flex-grow-1 me-1">{t("Document Processor")}</h5> */}
                </CardHeader>
                <CardBody>
                    {isCompleted === 2 ? (
                       <div className="text-center py-4">
                            <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '4.5rem' }}></i>
                            <h4 className="mt-3">{t("Processing Complete")}</h4>

                            <div className="summary-report mx-auto mt-4" style={{ maxWidth: '600px', textAlign: 'left' }}>
                                {/* Filter and display successful files */}
                                {processedFiles.filter(f => f.status === 'success').length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="mb-3">{t('SuccessfulUploads')} ({processedFiles.filter(f => f.status === 'success').length})</h5>
                                        <div className="list-group">
                                            {processedFiles.filter(f => f.status === 'success').map(file => (
                                                <div key={file.id} className="list-group-item list-group-item-action d-flex align-items-center">
                                                    <i className="ri-checkbox-circle-fill text-success fs-5 me-3"></i>
                                                    <div className="fw-medium">{file.name}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Filter and display failed files */}
                                {processedFiles.filter(f => f.status === 'error').length > 0 && (
                                    <div>
                                        <h5 className="mb-3">{t('FailedUploads')} ({processedFiles.filter(f => f.status === 'error').length})</h5>
                                        <div className="list-group">
                                            {processedFiles.filter(f => f.status === 'error').map(file => (
                                                <div key={file.id} className="list-group-item list-group-item-action d-flex flex-column align-items-start">
                                                    <div className="d-flex align-items-center w-100">
                                                        <i className="ri-close-circle-fill text-danger fs-5 me-3"></i>
                                                        <div className="fw-medium">{file.name}</div>
                                                    </div>
                                                    <div className="ps-4 ms-3 mt-1 text-muted">
                                                        <small>{t('Reason')}: {t(file.message, { defaultValue: file.message })}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-5">
                                <Button color="success" className="me-2 ms-2" onClick={handleClear}>
                                    <i className="ri-add-line align-middle me-1"></i> {t("Upload More Cases")}
                                </Button>

                                <Button color="primary" outline onClick={() => navigate("/ai/case")}>
                                    <i className="ri-file-list-3-line align-middle me-1"></i> {t("View All Cases")}
                                </Button>
                            </div>
                       </div>
                    ) : (
                      <>
                        <div className="stepper-wrapper">
                            {/* <div className={`stepper-item ${activeStep > 1 ? 'completed' : 'active'}`}>
                                <div className="step-counter">1</div>
                                <div className="step-name">{t("Classification")}</div>
                            </div> */}
                            <div className={`stepper-item ${activeStep >= 2 && filepondFiles.length ? 'completed' : activeStep == 2 ? 'active' : ''}`}>
                                <div className="step-counter">1</div>
                                <div className="step-name">{t("Upload Document")}</div>
                            </div>

                            { totalPages > MAX_PAGES ? 
                              <>
                                <div className={`stepper-item ${activeStep >= 3 ? 'completed' : activeStep == 3 ? 'active' : ''}`}>
                                  <div className="step-counter">3</div>
                                  <div className="step-name">{t("Split")}</div>
                                </div>
                                <div className={`stepper-item ${activeStep == 4 && isGeneratingDetails ? 'completed' : activeStep == 4 ? 'active' : ''}`}>
                                  <div className="step-counter">4</div>
                                  <div className="step-name">{t("Upload & Analyze")}</div>
                                </div>
                              </> : 
                              <div className={`stepper-item ${activeStep == 4 && isGeneratingDetails ? 'completed' : activeStep == 4 ? 'active' : ''}`}>
                                <div className="step-counter">2</div>
                                <div className="step-name">{t("Upload & Analyze")}</div>
                              </div>
                            }
                        </div>
                       <Form onSubmit={formik.handleSubmit}>
                            <Row className="justify-content-center">
                                <Col lg={7}>
                                  {/* --- STEP 3: SPLIT PDF --- */}
                                  { showSplitUI ? 
                                    <div className={`step-section h-100`}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            {/* This part is correct, the title stays at the start */}
                                            <h6>{t("Step2FileUpload")}</h6>

                                            {/* This container holds the buttons at the end */}
                                            <div className="d-flex gap-2">
                                                {isFileValid && !isAnalyzed && !isGeneratingDetails && (filepondFiles.length > 0) && (
                                                    <Button
                                                        color="danger"
                                                        outline
                                                        onClick={handleClear}
                                                        className={`d-flex align-items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                    >
                                                        <i className="ri-restart-line"></i>
                                                        <span>{t("Start over")}</span>
                                                    </Button>
                                                )}
                                                {isFileValid && !isAnalyzed && !isGeneratingDetails && (
                                                    <Button
                                                        color="success"
                                                        onClick={handleSplitAndAnalyze}
                                                        disabled={isGeneratingDetails}
                                                        className={`d-flex align-items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                    >
                                                        <i className="ri-magic-line"></i>
                                                        <span>{t("Analyze")}</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-4 border rounded p-3 bg-light">
                                            <h5>{t("Split PDF Configuration")}</h5>
                                            <p><strong>{t("File")}: </strong> {originalFileName} ({totalPages} {t("pages")})</p>
                        
                                            {splitRanges.map((range, idx) => (
                                                <Row key={idx} className="align-items-center mb-2">
                                                <Col md={4}>
                                                    <Input
                                                    type="text"
                                                    value={range.filename}
                                                    onChange={(e) => {
                                                        const newRanges = [...splitRanges];
                                                        newRanges[idx].filename = e.target.value;
                                                        setSplitRanges(newRanges);
                                                    }}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Input
                                                    type="number"
                                                    min={1}
                                                    max={totalPages}
                                                    value={range.from}
                                                    onChange={(e) => {
                                                      const value = Number(e.target.value) || '';
                                                      const newRanges = [...splitRanges];
                                                      newRanges[idx].from = value;

                                                      if (
                                                        newRanges[idx].to &&
                                                        value &&
                                                        newRanges[idx].to - value >= MAX_PAGES
                                                      ) {
                                                        toast.error(t('PageDifferenceMessage'))
                                                        return; // don't update if difference > MAX_PAGES
                                                      }

                                                      setSplitRanges(newRanges);
                                                    }}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                  <Input
                                                    type="number"
                                                    min={1}
                                                    max={totalPages}
                                                    value={range.to}
                                                    onChange={(e) => {
                                                      const value = Number(e.target.value) || '';
                                                      const newRanges = [...splitRanges];
                                                      newRanges[idx].to = value;

                                                      // check limit
                                                      if (
                                                        newRanges[idx].from &&
                                                        value &&
                                                        value - newRanges[idx].from >= MAX_PAGES
                                                      ) {
                                                        toast.error(t('PageDifferenceMessage'))
                                                        return; // don't update if difference > MAX_PAGES
                                                      }

                                                      setSplitRanges(newRanges);
                                                    }}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => setSplitRanges(splitRanges.filter((_, i) => i !== idx))}
                                                    >
                                                    {t("Remove")}
                                                    </Button>
                                                </Col>
                                                </Row>
                                            ))}
                    
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                onClick={handleRange}
                                            >
                                              {t("Add Range")}
                                            </Button>
                                        </div>
                                    </div> :
                                    <div className={`step-section h-100`}>
                                        <div className="upload-wrapper border rounded d-flex flex-column justify-content-center p-2" style={{ backgroundColor: '#f8f9fa' }}>
                                            {activeStep < 2 && (
                                                <div className="upload-overlay">
                                                    <i className={` ${isRTL ? 'ri-arrow-right-circle-line' : 'ri-arrow-left-circle-line'}`}></i>
                                                    <h5 className="mb-1">{t("Complete Step 1 First")}</h5>
                                                    <p className="text-muted">{t("Please select a category and sub-category to enable file upload")}</p>
                                                </div>
                                            )}
                                            {isGeneratingDetails ? (
                                                    isMultiUpload ? (
                                                      <div className="text-left p-4 w-100">
                                                        <h4 className="mb-3 text-center">{t("AnalyzingDocuments")}</h4>
                                                        <p className="text-muted mb-4 text-center">{t("YourFilesAreBeingProcessedSequentially")}</p>
                                                        { isCompleted == 0 || (isCompleted == 1 && !processedFiles.length) ?
                                                            <div className="d-flex justify-content-center align-items-center">
                                                              <Spinner
                                                                style={{ height: "3rem", width: "3rem" }}
                                                                className="me-2"
                                                              >
                                                                Loading...
                                                              </Spinner>
                                                            </div> :
                                                          <div className="multi-upload-progress-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                                            {processedFiles.filter((file) => file.status != 'retry').map(file => (
                                                                <Card key={file.id} className="mb-2 shadow-sm">
                                                                    <CardBody className="p-2">
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="flex-shrink-0 me-3 ms-1">
                                                                                {file.status === 'analyzing' && <Spinner size="sm" color="primary" />}
                                                                                {file.status === 'success' && <i className="ri-checkbox-circle-fill text-success fs-4"></i>}
                                                                                {file.status === 'error' && <i className="ri-close-circle-fill text-danger fs-4"></i>}
                                                                                {file.status === 'pending' && <i className="ri-time-line text-muted fs-4"></i>}
                                                                            </div>
                                                                            <div className="flex-grow-1 overflow-hidden">
                                                                                <h6 className="mb-1 text-truncate" title={file.name}>{file.name}</h6>
                                                                                <p className="mb-0 text-muted small">{t(file.message)}</p>
                                                                            </div>
                                                                            {/* Retry button only if error */}
                                                                            {file.status === "error" && (
                                                                              <Button size="sm" color="danger" onClick={() => handleRetry(file)}>
                                                                                {t("Retry")}
                                                                              </Button>
                                                                            )}
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            ))}
                                                            { isCompleted == 3 &&
                                                              <div className="d-flex justify-content-center mt-3">
                                                                <Button color="primary" outline onClick={handleComplete}>
                                                                  <i className="ri-file-list-3-line align-middle me-1"></i> {t("Complete")}
                                                                </Button>
                                                              </div>
                                                            }
                                                          </div>
                                                        }
                                                      </div>
                                                    ) : (
                                                        <div className="text-center p-4">
                                                            <h4 className="mb-3">{t("Analyzing Your Document")}</h4>
                                                            <p className="text-muted mb-4">{t("This may take a moment")}</p>
                                                            <div className="d-flex justify-content-center mb-4">
                                                                <div className="progress-ring" style={{'--progress': `${uploadProgress}%`}}>
                                                                    <div className="progress-ring-value">{uploadProgress}%</div>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 mx-auto" style={{ maxWidth: '400px' }}>
                                                                <ul className={`list-unstyled d-flex flex-column align-items-start`}>
                                                                    {processingLog.map((step) => (
                                                                        <li key={step.id} className="d-flex align-items-center mb-2">
                                                                            <i className="ri-check-line text-success me-2 ms-2"></i>
                                                                            <span className="text-muted small">{t(step.text)}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    )
                                                ) :  documentUploadError ? (
                                                    <div className="text-center p-5">
                                                        <Alert color="danger">
                                                            <i className="ri-error-warning-line fs-1"></i>
                                                            <h5 className="alert-heading mt-2">{t("Analysis Failed")}</h5>
                                                            <p>{t(documentUploadError) || t("An unknown error occurred")}</p>
                                                            <Button color="secondary" onClick={handleClear}>{t("Start over")}</Button>
                                                        </Alert>
                                                    </div>
                                                ) : (
                                                <div className="enhanced-filepond-container">
                                                <FilePond
                                                  ref={filepondRef}
                                                  files={filepondFiles}
                                                  // onaddfile={handleFilePondAddFile}
                                                  onupdatefiles={handleFilePondUpdate}
                                                  onwarning={handleFilePondWarning} 
                                                  allowMultiple={true}
                                                  // maxTotalFileSize={`${MAX_FILE_SIZE}B`} 
                                                  // maxFileSize={`${MAX_FILE_SIZE / (1024 * 1024)}MB`}
                                                  name="file"
                                                  acceptedFileTypes={['application/pdf', '.pdf']}
                                                  // disabled={!areCategoriesSelected || isGeneratingDetails}
                                                  disabled={isGeneratingDetails}
                                                  credits={false}
                                                  labelIdle={isRTL ? "اسحب وأفلت ملفاتك أو <span class='filepond--label-action'>استعرض</span>" : "Drag & drop your files or <span class='filepond--label-action'>Browse</span>"}
                                                  labelFileProcessing={isRTL ? "جارٍ المعالجة..." : "Processing..."}
                                                  labelMaxFileSizeExceeded={isRTL ? "الملف كبير جدًا" : "File is too large"}
                                                  labelMaxTotalFileSizeExceeded={isRTL ? "تم تجاوز الحجم الإجمالي للملفات" : "Maximum total file size exceeded"}
                                                  labelMaxFileSize={isRTL ? "الحد الأقصى لحجم الملف هو {filesize}" : "Maximum file size is {filesize}"}
                                                  labelFileTypeNotAllowed={isRTL ? "نوع الملف غير صالح" : "File is of invalid type"}
                                                  labelFileProcessingError={isRTL ? "حدث خطأ أثناء المعالجة" : "Error during processing"}
                                                />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                  }
                                </Col>
                            </Row>
                       </Form>
                      </>
                    )}
                </CardBody>
            </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CaseUpload;